/**
 * Utility functions for store management
 */
import { BaseState, StoreError } from '../types/common';

// Default base state
export const createBaseState = (): BaseState => ({
  isLoading: false,
  error: null,
  lastUpdated: null,
});

// Error handling utilities
export const createStoreError = (
  code: string,
  message: string,
  details?: Record<string, any>
): StoreError => ({
  code,
  message,
  details,
  timestamp: Date.now(),
});

// Async operation wrapper with error handling
export const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void
): Promise<T | null> => {
  try {
    setLoading(true);
    setError(null);
    const result = await operation();
    setLoading(false);
    return result;
  } catch (error) {
    setLoading(false);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    setError(errorMessage);
    console.error('Store operation failed:', error);
    return null;
  }
};

// Debounced function creator
export const createDebounced = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T => {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
};

// Deep comparison utility
export const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  
  if (typeof a === 'object') {
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    
    if (keysA.length !== keysB.length) return false;
    
    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!deepEqual(a[key], b[key])) return false;
    }
    
    return true;
  }
  
  return false;
};

// State update with change detection
export const updateWithChangeDetection = <T>(
  currentState: T,
  updates: Partial<T>,
  onChange?: (changes: Partial<T>) => void
): T => {
  const changes: Partial<T> = {};
  let hasChanges = false;
  
  for (const [key, value] of Object.entries(updates)) {
    if (!deepEqual(currentState[key as keyof T], value)) {
      changes[key as keyof T] = value as T[keyof T];
      hasChanges = true;
    }
  }
  
  if (hasChanges) {
    onChange?.(changes);
    return { ...currentState, ...changes, lastUpdated: Date.now() };
  }
  
  return currentState;
};

// Local storage helpers
export const getFromStorage = <T>(key: string, defaultValue: T): T => {
  // Guard against server-side execution
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const setInStorage = <T>(key: string, value: T): void => {
  // Guard against server-side execution
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
};

export const removeFromStorage = (key: string): void => {
  // Guard against server-side execution
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error);
  }
};

// Store middleware utilities
export const createPersistMiddleware = <T>(
  key: string,
  select?: (state: T) => Partial<T>
) => (config: any) => (set: any, get: any, api: any) => {
  const persistedState = getFromStorage(key, {});
  
  const newConfig = config(
    (update: any) => {
      set(update);
      const state = get();
      const stateToSave = select ? select(state) : state;
      setInStorage(key, stateToSave);
    },
    get,
    api
  );
  
  return { ...newConfig, ...persistedState };
};

// Validation utilities
export const createValidator = <T>(
  rules: Record<keyof T, (value: any) => string | null>
) => (data: T): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  for (const [field, validator] of Object.entries(rules) as [string, (value: any) => string | null][]) {
    const error = validator(data[field as keyof T]);
    if (error) {
      errors[field] = error;
    }
  }
  
  return errors;
};

// Retry utility
export const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error = new Error('No attempts made');
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  throw lastError;
};